import store from '@/store';
import {
  AccountOverviewConsolidatedInvoiceModel,
  AccountOverviewPayableDebtStatus,
  AccountReceivableType,
  BookingType,
  CompositeKeyModel,
  CompositeSubKeyModel,
  DirectDebitPaymentModel,
  PayDebtCheckModel,
  PaymentAgreenentModel,
  PaymentLinkModel,
  PaymentOptionsModel,
  PaymentProvidersEnum,
  PaymentResultModel,
  PayWayResponse,
  ShoppingBasketModel,
  AdyenSessionDbModel,
  AdyenAdvancedPaymentResultModel,
  AdyenAddress,
  WalletIdentityModel,
  ExpressJoinPrepareRequestModel,
  ExpressJoinPreparedModel,
  ExpressJoinClaimedModel,
  ExpressJoinBasketPreparedModel,
  ExpressJoinFinishedModel,
} from '../../../common/src';
import * as backendApi from '../shared/backend-api';

const baseUrl = window.location.origin;
export default class PaymentService {
  constructor() {}

  makeCCClipcardPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipcardSize: number | undefined,
    priceOverride: number | null,
    provider: PaymentProvidersEnum | undefined,
    externalId: string | undefined
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-clipcard-payment', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipcardSize,
      provider,
      baseUrl,
      priceOverride,
      externalId,
    });
  }

  makeCCShoppingBasketPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    shoppingBasketId: string,
    provider: PaymentProvidersEnum
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-shopping-basket-payment', {
      selectedUserId,
      selectedUserCenterId,
      shoppingBasketId,
      provider,
      baseUrl,
    });
  }

  makeCCClassPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipcardSize: number | undefined,
    bookingId: number,
    bookingCenterId: number,
    provider: PaymentProvidersEnum | undefined,
    externalId: string | undefined
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-class-booking-payment', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipcardSize,
      bookingId,
      bookingCenterId,
      provider,
      baseUrl,
      externalId,
    });
  }

  makeCCActivityBookingPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipcardSize: number | undefined,
    activityCenterId: number,
    activityId: number,
    resourceKey: number,
    startTime: string,
    date: string,
    duration: number,
    provider: PaymentProvidersEnum | undefined,
    externalId: string | undefined,
    bookingType: BookingType,
    instructorCenterId?: number
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-activity-booking-payment', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipcardSize,
      activityCenterId,
      activityId,
      resourceKey,
      startTime,
      date,
      duration,
      provider,
      baseUrl,
      externalId,
      bookingType,
      instructorCenterId,
    });
  }

  makeCoFClipcardPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipcardSize: number | undefined,
    priceOverride: number | null,
    selectedCardOnFile: PaymentAgreenentModel
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-clipcard-payment', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipcardSize,
      selectedCardOnFile,
      priceOverride,
    });
  }

  makeCoFClassPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipcardSize: number | undefined,
    bookingId: number,
    bookingCenterId: number,
    selectedCardOnFile: PaymentAgreenentModel
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-class-payment', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipcardSize,
      bookingId,
      bookingCenterId,
      selectedCardOnFile,
    });
  }

  makeCoFActivityBookingPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipcardSize: number | undefined,
    activityCenterId: number,
    activityId: number,
    resourceKey: number,
    startTime: string,
    date: string,
    duration: number,
    selectedCardOnFile: PaymentAgreenentModel,
    bookingType: BookingType,
    instructorCenterId?: number | undefined
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-activity-booking-payment', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipcardSize,
      activityCenterId,
      activityId,
      resourceKey,
      startTime,
      date,
      duration,
      selectedCardOnFile,
      bookingType,
      instructorCenterId,
    });
  }

  makeCCCoursePayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    provider: PaymentProvidersEnum | undefined
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-course-payment', {
      selectedUserId,
      selectedUserCenterId,
      provider,
      baseUrl,
    });
  }

  makeCoFCoursePayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    selectedCardOnFile: PaymentAgreenentModel
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-course-payment', { selectedUserId, selectedUserCenterId, selectedCardOnFile });
  }

  makePAandDepositForCoursePayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    provider: PaymentProvidersEnum,
    isDDPL: boolean
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-course-payment-create-pa-and-deposit', {
      baseUrl,
      selectedUserId,
      selectedUserCenterId,
      provider,
      isDDPL,
    });
  }

  makeCCCampsPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    provider: PaymentProvidersEnum | undefined,
    selectedPaymentOption: PaymentOptionsModel | null
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-camps-payment', {
      selectedUserId,
      selectedUserCenterId,
      provider,
      baseUrl,
      selectedPaymentOption,
    });
  }

  makeCoFCampsPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    selectedCardOnFile: PaymentAgreenentModel,
    selectedPaymentOption: PaymentOptionsModel | null
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-camps-payment', {
      selectedUserId,
      selectedUserCenterId,
      selectedCardOnFile,
      selectedPaymentOption,
    });
  }

  makePAandDepositForCampsPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    provider: PaymentProvidersEnum,
    selectedPaymentOption: PaymentOptionsModel | null,
    isDDPL: boolean
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-camps-payment-create-pa-and-deposit', {
      baseUrl,
      selectedUserId,
      selectedUserCenterId,
      provider,
      selectedPaymentOption,
      isDDPL,
    });
  }

  makePLUpdateAgreementPayment(
    provider: PaymentProvidersEnum,
    paymentAgreementKeyToUpdate: CompositeSubKeyModel,
    isDDPaymentLink: boolean
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-pl-update-agreement-payment', {
      baseUrl,
      provider,
      paymentAgreementKeyToUpdate,
      isDDPaymentLink,
    });
  }

  makeACHUpdatePaymentAgreement(
    selectedDirectDebit: DirectDebitPaymentModel,
    paymentAgreementKeyToUpdate: CompositeSubKeyModel
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-dd-update-agreement-payment', {
      selectedDirectDebit,
      paymentAgreementKeyToUpdate,
    });
  }

  makePLNewSubscriptionPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    provider: PaymentProvidersEnum,
    type: 'NEW' | 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    clipCardId?: string,
    clipCardSize?: string,
    isDDPaymentLink?: boolean,
    referAFriend?: string,
    recurringClipcardSubClipSize?: number,
    recurringClipcardSubStartupClipcard?: number,
    /** addOnId -> the center that add-on is bought for; add-ons not listed stay on subscriptionCenterId. */
    addOnTargetCenters?: Record<number, number>
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-pl-new-subscription-payment', {
      baseUrl,
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      provider,
      type,
      selectedUserId,
      selectedUserCenterId,
      clipCardId,
      clipCardSize,
      isDDPaymentLink,
      referAFriend,
      recurringClipcardSubClipSize,
      recurringClipcardSubStartupClipcard,
      addOnTargetCenters,
    });
  }

  makeCCCASHUpfrontPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    selectedFeesCreditCard: PaymentProvidersEnum,
    type: 'NEW' | 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    selectedPaymentOptionExternalId?: string,
    clipCardId?: string,
    clipCardSize?: string,
    referAFriend?: string,
    /** addOnId -> the center that add-on is bought for; add-ons not listed stay on subscriptionCenterId. */
    addOnTargetCenters?: Record<number, number>
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-cash-upfront-subscription-payment', {
      baseUrl,
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      selectedFeesCreditCard,
      type,
      selectedUserId,
      selectedUserCenterId,
      selectedPaymentOptionExternalId,
      clipCardId,
      clipCardSize,
      referAFriend,
      addOnTargetCenters,
    });
  }

  makeCoFCASHUpfrontPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    selectedFeesCoF: PaymentAgreenentModel,
    type: 'NEW' | 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    selectedPaymentOptionExternalId?: string,
    clipCardId?: string,
    clipCardSize?: string
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-cash-upfront-subscription-payment', {
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      selectedFeesCoF,
      type,
      selectedUserId,
      selectedUserCenterId,
      selectedPaymentOptionExternalId,
      clipCardId,
      clipCardSize,
    });
  }

  makeCCEFTUpfrontPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    selectedFeesCreditCard: PaymentProvidersEnum,
    type: 'NEW' | 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    clipCardId?: string,
    clipCardSize?: string,
    recurringClipcardSubClipSize?: number,
    recurringClipcardSubStartupClipcard?: number,
    /** addOnId -> the center that add-on is bought for; add-ons not listed stay on subscriptionCenterId. */
    addOnTargetCenters?: Record<number, number>
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-eft-upfront-subscription-payment', {
      baseUrl,
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      selectedFeesCreditCard,
      type,
      selectedUserId,
      selectedUserCenterId,
      clipCardId,
      clipCardSize,
      recurringClipcardSubClipSize,
      recurringClipcardSubStartupClipcard,
      addOnTargetCenters,
    });
  }

  makeCoFEFTUpfrontPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    selectedFeesCoF: PaymentAgreenentModel,
    type: 'NEW' | 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    clipCardId?: string,
    clipCardSize?: string,
    recurringClipcardSubClipSize?: number,
    recurringClipcardSubStartupClipcard?: number
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-eft-upfront-subscription-payment', {
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      selectedFeesCoF,
      type,
      selectedUserId,
      selectedUserCenterId,
      clipCardId,
      clipCardSize,
      recurringClipcardSubClipSize,
      recurringClipcardSubStartupClipcard,
    });
  }

  makeACHNewSubscriptionPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    selectedDirectDebit: DirectDebitPaymentModel,
    selectedFeesCreditCard: PaymentProvidersEnum | PaymentAgreenentModel,
    type: 'NEW' | 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    clipCardId?: string,
    clipCardSize?: string,
    referAFriend?: string,
    recurringClipcardSubClipSize?: number,
    recurringClipcardSubStartupClipcard?: number,
    /** addOnId -> the center that add-on is bought for; add-ons not listed stay on subscriptionCenterId. */
    addOnTargetCenters?: Record<number, number>
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-ach-new-subscription-payment', {
      baseUrl,
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      selectedDirectDebit,
      selectedFeesCreditCard,
      type,
      selectedUserId,
      selectedUserCenterId,
      clipCardId,
      clipCardSize,
      referAFriend,
      recurringClipcardSubClipSize,
      recurringClipcardSubStartupClipcard,
      addOnTargetCenters,
    });
  }

  makeInvoiceUpfrontPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    selectedFeesCreditCard: PaymentProvidersEnum,
    type: 'ADD',
    selectedUserId?: number,
    selectedUserCenterId?: number,
    clipCardId?: string,
    clipCardSize?: string,
    referAFriend?: string,
    recurringClipcardSubClipSize?: number,
    recurringClipcardSubStartupClipcard?: number,
    /** addOnId -> the center that add-on is bought for; add-ons not listed stay on subscriptionCenterId. */
    addOnTargetCenters?: Record<number, number>
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-invoice-new-subscription-payment', {
      baseUrl,
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      type,
      selectedFeesCreditCard,
      selectedUserId,
      selectedUserCenterId,
      clipCardId,
      clipCardSize,
      referAFriend,
      recurringClipcardSubClipSize,
      recurringClipcardSubStartupClipcard,
      addOnTargetCenters,
    });
  }

  makeCCDebtPayment(
    consolidatedInvoicesArray: AccountOverviewConsolidatedInvoiceModel[],
    accountOverviewPayableDebtStatus: AccountOverviewPayableDebtStatus,
    provider: PaymentProvidersEnum,
    payableDebtAmount: number,
    debtType: AccountReceivableType
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-debt-payment', {
      consolidatedInvoicesArray,
      baseUrl,
      accountOverviewPayableDebtStatus,
      provider,
      payableDebtAmount,
      debtType,
    });
  }

  preflightSADebtPayment(userExternalID: string, amount: number, consolidatedInv: string | undefined): Promise<PayDebtCheckModel> {
    return backendApi.post('payment/preflight-sa-debt-payment', {
      userExternalID,
      amount,
      consolidatedInv,
    });
  }

  makeSACCDebtPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    staffEmpNumber: string,
    sessionKey: string,
    amount: number,
    consolidatedInv: string | undefined,
    paymentProvider: PaymentProvidersEnum
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-sa-debt-payment', {
      selectedUserId,
      selectedUserCenterId,
      baseUrl,
      staffEmpNumber,
      sessionKey,
      amount,
      consolidatedInv,
      paymentProvider,
    });
  }

  makeCoFChangeSubscriptionPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    currentPlanId: number,
    currentPlanCenterId: number,
    selectedCardOnFile: PaymentAgreenentModel
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-change-subscription-payment', {
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      currentPlanId,
      currentPlanCenterId,
      selectedCardOnFile,
    });
  }

  makeChangeSubscriptionPayment(
    subscriptionCenterId: number,
    subscriptionId: number,
    startupDate: string,
    addOns: number[],
    promoCode: string,
    currentPlanId: number,
    currentPlanCenterId: number,
    feesProvider: PaymentProvidersEnum,
    newProviderForPaymentAgreement: PaymentProvidersEnum | 'INVOICE' | DirectDebitPaymentModel | undefined
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-change-subscription-payment', {
      baseUrl,
      subscriptionCenterId,
      subscriptionId,
      startupDate,
      addOns,
      promoCode,
      currentPlanId,
      currentPlanCenterId,
      feesProvider: feesProvider,
      newProviderForPaymentAgreement: newProviderForPaymentAgreement,
    });
  }

  startClipcardSubscriptionPaymentSession(
    selectedUserId: number,
    selectedUserCenterId: number,
    subscriptionCenterId: number,
    shoppingBasketId: string
  ): Promise<ShoppingBasketModel> {
    return backendApi.post('payment/start-clipcard-subscription-payment-session', {
      selectedUserId,
      selectedUserCenterId,
      subscriptionCenterId,
      shoppingBasketId,
    });
  }

  createStaffClipcardShoppingBasket(
    selectedUserId: number,
    selectedUserCenterId: number,
    clipcardId: number,
    clipcardCenterId: number,
    clipSize: number | undefined,
    priceOverride: number | null
  ): Promise<ShoppingBasketModel> {
    return backendApi.post('payment/create-clipcard-staff-shopping-basket', {
      selectedUserId,
      selectedUserCenterId,
      clipcardId,
      clipcardCenterId,
      clipSize,
      priceOverride,
    });
  }

  makeACHCreatePaymentAgreement(selectedDirectDebit: DirectDebitPaymentModel): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-dd-create-agreement-payment', {
      selectedDirectDebit,
    });
  }

  makePLCreatePaymentAgreement(selectedCreditCard: PaymentProvidersEnum, isDDPaymentLink: boolean): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-pl-create-agreement-payment', {
      baseUrl,
      selectedCreditCard,
      isDDPaymentLink,
    });
  }

  makeInvoiceCreatePaymentAgreement(): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-invoice-create-agreement-payment', {});
  }

  makeInvoiceUpdatePaymentAgreement(paymentAgreementKeyToUpdate: CompositeSubKeyModel): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-invoice-update-agreement-payment', { paymentAgreementKeyToUpdate });
  }

  makeCCTopUpPayment(paymentProvider: PaymentProvidersEnum, topUpAmount: number): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-top-up-account', {
      baseUrl,
      paymentProvider,
      topUpAmount,
    });
  }

  makeCCFreezeSubscriptionPayment(
    paymentProvider: PaymentProvidersEnum,
    fromDate: string,
    toDate: string,
    subscriptionId: number,
    subscriptionCenterId: number
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-freeze-subscription', {
      baseUrl,
      paymentProvider,
      fromDate,
      toDate,
      subscriptionId,
      subscriptionCenterId,
    });
  }

  checkPayment(
    selectedUserId: number,
    selectedUserCenterId: number,
    paymentId: string | null,
    transId: string | null,
    provider: PaymentProvidersEnum,
    isSuccess: boolean,
    paymentProviderErrorDesc: string | null,
    providerRawResult?: Record<string, string> | null
  ): Promise<PaymentResultModel> {
    return backendApi.post('payment/check-payment', {
      selectedUserId,
      selectedUserCenterId,
      paymentId,
      transId,
      provider,
      isSuccess,
      paymentProviderErrorDesc,
      providerRawResult,
    });
  }

  // this is only called by the Adyen dropin component, there's no way we can test it
  /* istanbul ignore next */
  finalizeAdyenSession(
    paymentKey: string,
    sessionResult: string,
    billingAddress: AdyenAddress | null,
    /** Only the express join sends this: the name, phone and address the wallet released, for the
     *  member who typed none of them. */
    walletIdentity?: WalletIdentityModel | null
  ): Promise<AdyenSessionDbModel> {
    return backendApi.post<AdyenSessionDbModel>('adyen/finalize-session', {
      paymentKey,
      sessionResult,
      billingAddress,
      walletIdentity: walletIdentity || undefined,
    });
  }

  // this is only called by the Adyen dropin component, there's no way we can test it
  /* istanbul ignore next */
  submitAdyenPaymentDetails(paymentKey: string, details: Record<string, string>): Promise<AdyenAdvancedPaymentResultModel> {
    return backendApi.post<AdyenAdvancedPaymentResultModel>('adyen/payments/details', { paymentKey, details });
  }

  /**
   * The Advanced payment call, made by the server on our behalf.
   *
   * Only the payment method, the browser's own description of itself and the origin go up. What is
   * charged, to whom, and under which reference are all read from the record the server wrote when
   * it opened the payment — this call cannot influence any of them.
   */
  // only reachable from inside a wallet callback, which no unit test can enter
  /* istanbul ignore next */
  submitAdyenPayment(
    paymentKey: string,
    paymentMethod: Record<string, unknown>,
    browserInfo?: Record<string, unknown>
  ): Promise<AdyenAdvancedPaymentResultModel> {
    return backendApi.post<AdyenAdvancedPaymentResultModel>('adyen/payments', {
      paymentKey,
      paymentMethod,
      browserInfo,
      origin: window.location.origin,
    });
  }

  // this is only called by the Adyen dropin component, there's no way we can test it
  /* istanbul ignore next */
  checkAdyenPayment(paymentKey: string): Promise<AdyenSessionDbModel | null> {
    return backendApi.post<AdyenSessionDbModel | null>('adyen/adyen-check-payment', { paymentKey });
  }

  /**
   * The express wallet join, in the order it happens.
   *
   * `prepare` runs on the Pay press and makes no call to Adyen, so the wallet button appears without
   * the session round trip the ordinary path waits on. `claim` runs inside the wallet authorization
   * callback, while basket preparation runs from onSubmit before Advanced /payments. `finish` runs
   * after authorisation and reuses the prepared payment cache while capture remains pending.
   */
  prepareExpressJoin(request: ExpressJoinPrepareRequestModel & { baseUrl: string }): Promise<ExpressJoinPreparedModel> {
    return backendApi.post<ExpressJoinPreparedModel>('express-join/prepare', request);
  }

  invalidateExpressJoin(paymentKey: string): Promise<void> {
    return backendApi.post<void>('express-join/invalidate', { paymentKey });
  }

  // only reachable from inside a wallet callback, which no unit test can enter
  /* istanbul ignore next */
  claimExpressJoin(paymentKey: string, email: string, walletIdentity?: WalletIdentityModel): Promise<ExpressJoinClaimedModel> {
    return backendApi.post<ExpressJoinClaimedModel>('express-join/claim', { paymentKey, email, walletIdentity });
  }

  /** Prepare and persist the final basket before the wallet submits Advanced /payments. */
  prepareExpressJoinBasket(paymentKey: string): Promise<ExpressJoinBasketPreparedModel> {
    return backendApi.post<ExpressJoinBasketPreparedModel>('express-join/prepare-basket', { paymentKey });
  }

  // only reachable after a real wallet authorisation
  /* istanbul ignore next */
  finishExpressJoin(paymentKey: string, walletIdentity: WalletIdentityModel | null): Promise<ExpressJoinFinishedModel> {
    return backendApi.post<ExpressJoinFinishedModel>('express-join/finish', {
      paymentKey,
      walletIdentity: walletIdentity || undefined,
    });
  }

  // this is only called by the PayWay Frame, there's no way we can test it
  /* istanbul ignore next */
  makePaywayPayment(singleUseTokenId: string, key: string): Promise<PayWayResponse> {
    return backendApi.post('payway/payway-make-payment', { key, singleUseTokenId });
  }

  makeBuyAddonPayment(
    addOns: number[],
    subscriptionId: string,
    subscriptionCenterId: number,
    feesProvider: PaymentProvidersEnum
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-addon-payment', {
      baseUrl,
      feesProvider: feesProvider,
      addOns,
      subscriptionId,
      subscriptionCenterId,
    });
  }

  makeCoFBuyAddonPayment(
    addOns: number[],
    subscriptionId: string,
    subscriptionCenterId: number,
    selectedCardOnFile: PaymentAgreenentModel
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cof-addon-payment', {
      addOns,
      subscriptionId,
      subscriptionCenterId,
      selectedCardOnFile,
    });
  }

  makeCCTerminateBuyOutPayment(
    provider: PaymentProvidersEnum,
    subscriptionId: number,
    subscriptionCenterId: number,
    terminationDate: string
  ): Promise<PaymentLinkModel> {
    return backendApi.post('payment/make-cc-terminate-buy-out-payment', {
      baseUrl,
      provider,
      subscriptionId,
      subscriptionCenterId,
      terminationDate,
    });
  }
}
